import type { TSchmancyTheme } from './theme.interface'

export const LIGHT = {
	surface: [0.98, 0.87, 0.98, 1, 0.96, 0.94, 0.92, 0.9],
	on: 0.16,
	onVariant: 0.35,
	inverse: 0.25,
	inverseOn: 0.96,
	role: 0.48,
	roleOn: 1,
	container: 0.92,
	onContainer: 0.18,
	outline: 0.56,
	outlineVariant: 0.84,
	identity: [0.92, 0.035],
}

export const DARK = {
	surface: [0.15, 0.12, 0.27, 0.1, 0.18, 0.21, 0.24, 0.27],
	on: 0.92,
	onVariant: 0.82,
	inverse: 0.92,
	inverseOn: 0.25,
	role: 0.8,
	roleOn: 0.18,
	container: 0.36,
	onContainer: 0.92,
	outline: 0.62,
	outlineVariant: 0.3,
	identity: [0.36, 0.055],
}

export const FIXED = { fixed: 0.92, fixedDim: 0.84, onFixed: 0.18, onFixedVariant: 0.35 }

export const CHROMA = {
	secondary: { limit: 0.055, scale: 0.35 },
	tertiary: { limit: 0.09, scale: 0.55 },
	neutral: { limit: 0.012, scale: 0.05 },
	neutralVariant: { limit: 0.024, scale: 0.1 },
}

export const HUE_ROTATION = Math.PI / 3
export const ACHROMATIC_IDENTITY_HUE = (3 * Math.PI) / 2

// The numeric names are shade labels; the values are OKLCH lightness.
export const REFERENCE_LIGHTNESS = {
	'0': 0,
	'10': 0.18,
	'20': 0.25,
	'30': 0.36,
	'40': 0.48,
	'50': 0.58,
	'60': 0.66,
	'70': 0.73,
	'80': 0.8,
	'90': 0.92,
	'95': 0.96,
	'99': 0.99,
	'100': 1,
}

export const MODE_COLORS = {
	light: {
		error: {
			default: '#ba1a1a',
			on: '#ffffff',
			container: '#ffdad6',
			onContainer: '#410002',
			pinned: '#ba1a1a',
			onPinned: '#ffffff',
		},
		success: {
			default: '#006e1c',
			on: '#ffffff',
			container: '#94f990',
			onContainer: '#002204',
			pinned: '#006e1c',
			onPinned: '#ffffff',
		},
		warning: {
			default: '#8b5000',
			on: '#ffffff',
			container: '#ffdcbe',
			onContainer: '#2c1600',
			pinned: '#8b5000',
			onPinned: '#ffffff',
		},
		info: {
			default: '#0061a4',
			on: '#ffffff',
			container: '#d1e4ff',
			onContainer: '#001d36',
			pinned: '#0061a4',
			onPinned: '#ffffff',
		},
		series: {
			'0': {
				default: '#2a78d6',
				on: '#000000',
			},
			'1': {
				default: '#eb6834',
				on: '#000000',
			},
			'2': {
				default: '#1baf7a',
				on: '#000000',
			},
			'3': {
				default: '#eda100',
				on: '#000000',
			},
			'4': {
				default: '#e87ba4',
				on: '#000000',
			},
			'5': {
				default: '#008300',
				on: '#ffffff',
			},
		},
	},
	dark: {
		error: {
			default: '#ffb4ab',
			on: '#690005',
			container: '#93000a',
			onContainer: '#ffdad6',
			pinned: '#ba1a1a',
			onPinned: '#ffffff',
		},
		success: {
			default: '#78dc77',
			on: '#00390a',
			container: '#005313',
			onContainer: '#94f990',
			pinned: '#006e1c',
			onPinned: '#ffffff',
		},
		warning: {
			default: '#ffb870',
			on: '#4a2800',
			container: '#693c00',
			onContainer: '#ffdcbe',
			pinned: '#8b5000',
			onPinned: '#ffffff',
		},
		info: {
			default: '#9ecaff',
			on: '#003258',
			container: '#00497d',
			onContainer: '#d1e4ff',
			pinned: '#0061a4',
			onPinned: '#ffffff',
		},
		series: {
			'0': {
				default: '#3987e5',
				on: '#000000',
			},
			'1': {
				default: '#d95926',
				on: '#000000',
			},
			'2': {
				default: '#199e70',
				on: '#000000',
			},
			'3': {
				default: '#c98500',
				on: '#000000',
			},
			'4': {
				default: '#d55181',
				on: '#000000',
			},
			'5': {
				default: '#008300',
				on: '#ffffff',
			},
		},
	},
} satisfies Record<
	'light' | 'dark',
	Pick<TSchmancyTheme['sys']['color'], 'error' | 'success' | 'warning' | 'info' | 'series'>
>

export function themeConstants(dark: boolean): Omit<TSchmancyTheme['sys'], 'color'> {
	const value = {
		control: { border: 'var(--schmancy-sys-color-outlineVariant)' },
		typography: {
			display: {
				large: 'display-large',
				medium: 'display-medium',
				small: 'display-small',
			},
			headline: {
				large: 'headline-large',
				medium: 'headline-medium',
				small: 'headline-small',
			},
			title: {
				large: 'title-large',
				medium: 'title-medium',
				small: 'title-small',
			},
			body: {
				large: 'body-large',
				medium: 'body-medium',
				small: 'body-small',
			},
			label: {
				large: 'label-large',
				medium: 'label-medium',
				small: 'label-small',
			},
		},
		elevation: {
			'0': '0 0 0 0 rgba(0, 0, 0, 0)',
			'1': '0 2px 12px -2px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 15%, transparent)',
			'2': '0 4px 20px -2px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 22%, transparent)',
			'3': '0 8px 32px -4px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 28%, transparent)',
			'4': '0 12px 44px -4px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 35%, transparent)',
			'5': '0 20px 60px -4px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 42%, transparent)',
		},
		glass: {
			fill: '50%',
			blur: '24px',
			saturate: '180%',
			border: 'color-mix(in srgb, var(--schmancy-sys-color-surface-on) 12%, transparent)',
		},
		motion: {
			easing: {
				emphasized: 'cubic-bezier(0.2, 0.0, 0, 1.0)',
				emphasizedDecelerate: 'cubic-bezier(0.05, 0.7, 0.1, 1.0)',
				emphasizedAccelerate: 'cubic-bezier(0.3, 0.0, 0.8, 0.15)',
				standard: 'cubic-bezier(0.2, 0.0, 0, 1.0)',
				standardDecelerate: 'cubic-bezier(0, 0, 0, 1)',
				standardAccelerate: 'cubic-bezier(0.3, 0, 1, 1)',
				legacy: 'cubic-bezier(0.4, 0, 0.2, 1)',
				linear: 'linear',
			},
			duration: {
				short1: '50ms',
				short2: '100ms',
				short3: '150ms',
				short4: '200ms',
				medium1: '250ms',
				medium2: '300ms',
				medium3: '350ms',
				medium4: '400ms',
				long1: '450ms',
				long2: '500ms',
				long3: '550ms',
				long4: '600ms',
				extraLong1: '700ms',
				extraLong2: '800ms',
				extraLong3: '900ms',
				extraLong4: '1000ms',
			},
		},
		state: {
			opacity: {
				hover: '0.08',
				focus: '0.12',
				pressed: '0.12',
				dragged: '0.16',
				disabled: '0.38',
				disabledContainer: '0.12',
			},
		},
		spacing: {
			'0': '0',
			'1': '4px',
			'2': '8px',
			'3': '12px',
			'4': '16px',
			'5': '20px',
			'6': '24px',
			'7': '28px',
			'8': '32px',
			'9': '36px',
			'10': '40px',
			'11': '44px',
			'12': '48px',
		},
		outline: {
			'1': '1px',
		},
	}
	if (dark)
		value.elevation = {
			'0': '0 0 0 0 rgba(0, 0, 0, 0)',
			'1': '0 2px 12px -2px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 18%, transparent)',
			'2': '0 4px 20px -2px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 26%, transparent)',
			'3': '0 8px 32px -4px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 32%, transparent)',
			'4': '0 12px 44px -4px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 40%, transparent)',
			'5': '0 20px 60px -4px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 48%, transparent)',
		}
	return value
}
